import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtQty } from "../lib/api";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { RefreshCw, ExternalLink, Zap, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";

/**
 * IBKRLivePanel — live view of your real IBKR account via MCP.
 * Shows account summary + open positions + recent drafts pending confirmation in IBKR.
 * Auto-refreshes every 30s while mounted.
 */
export default function IBKRLivePanel() {
  const [connected, setConnected] = useState(null);
  const [accountType, setAccountType] = useState(null); // "live" | "paper" | null
  const [summary, setSummary] = useState(null);
  const [positions, setPositions] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [debug, setDebug] = useState(false);
  const [tools, setTools] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const s = await api.get("/ibkr/mcp/status");
      const isConn = !!s.data?.connected;
      setConnected(isConn);
      setAccountType(s.data?.account_type || null);
      if (!isConn) { setLoading(false); return; }
      const [sumRes, posRes, drRes] = await Promise.allSettled([
        api.get("/ibkr/mcp/summary"),
        api.get("/ibkr/mcp/positions"),
        api.get("/ibkr/mcp/drafts"),
      ]);
      const errs = [];
      if (sumRes.status === "fulfilled") setSummary(sumRes.value.data);
      else errs.push("summary: " + (sumRes.reason?.response?.data?.detail || sumRes.reason?.message || "failed"));
      if (posRes.status === "fulfilled") setPositions(posRes.value.data);
      else errs.push("positions: " + (posRes.reason?.response?.data?.detail || posRes.reason?.message || "failed"));
      if (drRes.status === "fulfilled") setDrafts(drRes.value.data?.drafts || []);
      if (errs.length) setErr(errs.join(" · "));
      setRefreshedAt(new Date());
    } catch (e) {
      setErr(e?.response?.data?.detail || "IBKR MCP error");
    } finally {
      setLoading(false);
    }
  }, []);

  const setType = async (t) => {
    try {
      await api.post("/ibkr/mcp/account-type", { account_type: t });
      setAccountType(t);
    } catch { /* ignore */ }
  };

  const loadTools = async () => {
    try {
      const { data } = await api.get("/ibkr/mcp/tools");
      setTools(data);
    } catch (e) {
      setTools({ error: e?.response?.data?.detail || String(e) });
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  if (connected === null) return null;
  if (!connected) {
    return (
      <Card className="p-4 rounded-2xl border-border/60" data-testid="ibkr-live-panel">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-secondary/50 flex items-center justify-center text-xs font-bold">IBKR</div>
            <div>
              <div className="font-display font-bold text-sm">Live IBKR account</div>
              <div className="text-xs text-muted-foreground">Connect to see your real positions and draft trades.</div>
            </div>
          </div>
          <Link to="/settings" data-testid="ibkr-live-connect-link">
            <Button size="sm" className="rounded-full">Connect IBKR</Button>
          </Link>
        </div>
      </Card>
    );
  }

  const posList = extractPositionsList(positions);
  const sum = extractSummary(summary);

  return (
    <Card className="p-4 rounded-2xl border-emerald-500/25 bg-emerald-500/[0.02]" data-testid="ibkr-live-panel">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          </div>
          <div>
            <div className="font-display font-bold text-sm flex items-center gap-2 flex-wrap">
              Your IBKR account
              {accountType ? (
                <Badge
                  data-testid={`ibkr-acct-label-${accountType}`}
                  className={`border-0 text-[10px] font-mono-tab uppercase tracking-wider ${
                    accountType === "live"
                      ? "bg-emerald-500/15 text-emerald-400"
                      : "bg-amber-500/15 text-amber-400"
                  }`}>
                  {accountType === "live" ? "Live" : "Paper"}
                </Badge>
              ) : (
                <div className="inline-flex bg-secondary/60 rounded-full p-0.5 gap-0.5" data-testid="ibkr-acct-picker">
                  <button onClick={() => setType("live")}
                          data-testid="ibkr-acct-pick-live"
                          className="px-2 py-0.5 text-[10px] font-mono-tab uppercase tracking-wider rounded-full hover:bg-emerald-500/20 hover:text-emerald-400 transition-colors">
                    Live?
                  </button>
                  <button onClick={() => setType("paper")}
                          data-testid="ibkr-acct-pick-paper"
                          className="px-2 py-0.5 text-[10px] font-mono-tab uppercase tracking-wider rounded-full hover:bg-amber-500/20 hover:text-amber-400 transition-colors">
                    Paper?
                  </button>
                </div>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              via native MCP · refreshed {refreshedAt ? refreshedAt.toLocaleTimeString() : "…"}
              {accountType && (
                <button onClick={() => setType(accountType === "live" ? "paper" : "live")}
                        data-testid="ibkr-acct-swap"
                        className="ml-2 underline hover:text-foreground">
                  switch to {accountType === "live" ? "Paper" : "Live"}
                </button>
              )}
            </div>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading}
                data-testid="ibkr-live-refresh"
                className="h-8 rounded-full">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3" data-testid="ibkr-live-summary">
        <Tile label="Net liq" value={sum.netLiq != null ? fmtMoney(sum.netLiq) : "—"} />
        <Tile label="Cash" value={sum.cash != null ? fmtMoney(sum.cash) : "—"} />
        <Tile label="Buying power" value={sum.buyingPower != null ? fmtMoney(sum.buyingPower) : "—"} />
        <Tile label="Day P&L" value={sum.dayPnl != null ? fmtMoney(sum.dayPnl) : "—"}
              accent={sum.dayPnl != null ? (sum.dayPnl >= 0 ? "gain" : "loss") : null} />
      </div>

      {/* Pending drafts */}
      {drafts.length > 0 && (
        <div className="mb-3 p-2.5 rounded-lg border border-amber-500/30 bg-amber-500/[0.05]" data-testid="ibkr-live-drafts">
          <div className="flex items-center gap-2 mb-1.5">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
            <div className="text-xs font-semibold text-amber-300">
              {drafts.length} draft{drafts.length > 1 ? "s" : ""} awaiting your confirmation in IBKR
            </div>
          </div>
          <div className="text-[11px] text-muted-foreground leading-relaxed">
            Open IBKR mobile or desktop → <span className="font-semibold text-foreground">Orders &amp; Trades → AI Instructions</span> to review and confirm.
          </div>
          <ul className="mt-1.5 space-y-0.5 text-[11px] font-mono-tab">
            {drafts.slice(0, 3).map((d, i) => (
              <li key={i} className="flex justify-between text-muted-foreground">
                <span>{d.side?.toUpperCase()} {fmtQty(d.quantity)} {d.symbol}</span>
                <span>{d.limit_price ? `@ ${fmtMoney(d.limit_price)}` : "MKT"}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Positions */}
      <div>
        <div className="text-[10px] font-mono-tab uppercase tracking-wider text-muted-foreground mb-1.5">
          Open positions
        </div>
        {posList === null ? (
          <div className="text-xs text-muted-foreground italic py-2">Fetching...</div>
        ) : posList.length === 0 ? (
          <div className="text-xs text-muted-foreground italic py-2">No open positions in your IBKR account right now.</div>
        ) : (
          <div className="rounded-lg border border-border/40 divide-y divide-border/40 overflow-hidden" data-testid="ibkr-live-positions">
            {posList.slice(0, 8).map((p, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{p.symbol || p.ticker || p.contract || "?"}</span>
                  <span className="text-muted-foreground text-[10px]">{p.assetClass || p.asset_type || ""}</span>
                </div>
                <div className="flex items-center gap-3 font-mono-tab">
                  <span>{fmtQty(p.quantity ?? p.position ?? p.size ?? 0)}</span>
                  {p.marketValue != null && <span>{fmtMoney(p.marketValue)}</span>}
                  {(p.unrealizedPnl != null || p.dayPnl != null) && (
                    <span className={(p.unrealizedPnl ?? p.dayPnl) >= 0 ? "gain" : "loss"}>
                      {fmtMoney(p.unrealizedPnl ?? p.dayPnl)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Errors + diagnostic */}
      {err && (
        <div className="mt-3 p-2.5 rounded-lg border border-red-500/30 bg-red-500/[0.05] text-[11px] text-red-300" data-testid="ibkr-live-error">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold">Couldn't read one or more IBKR feeds</div>
              <div className="opacity-80 mt-0.5">{err}</div>
              <button onClick={() => setDebug(!debug)} className="mt-1 underline underline-offset-2 hover:text-red-200">
                {debug ? "Hide" : "Show"} diagnostic
              </button>
            </div>
          </div>
          {debug && (
            <div className="mt-2 pt-2 border-t border-red-500/20 space-y-1">
              <Button size="sm" variant="outline" onClick={loadTools} data-testid="ibkr-live-tools-btn"
                      className="h-7 text-[11px] rounded-full border-red-500/30">
                List MCP tools IBKR exposes
              </Button>
              {tools && (
                <pre className="text-[10px] bg-black/40 p-2 rounded-md overflow-x-auto max-h-40 mt-1.5">
                  {JSON.stringify(tools, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
      )}

      <div className="mt-3 flex items-center gap-2 text-[10px] text-muted-foreground">
        <Zap className="w-3 h-3" />
        <span>Aanar never places live orders directly — every trade appears as a draft in IBKR for you to confirm.</span>
      </div>

      {/* Always-visible diagnostic: click to reveal the exact MCP payload IBKR returned */}
      <div className="mt-3 pt-3 border-t border-border/40">
        <button onClick={() => { setDebug(!debug); if (!tools) loadTools(); }}
                data-testid="ibkr-live-diagnose-btn"
                className="w-full flex items-center justify-between text-[11px] text-muted-foreground hover:text-foreground transition-colors">
          <span className="flex items-center gap-1.5">
            {debug ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            Diagnose · show what IBKR sent back
          </span>
          <span className="opacity-60">
            {positions?.tool && `positions: ${positions.tool}`}
            {summary?.tool && ` · summary: ${summary.tool}`}
          </span>
        </button>
        {debug && (
          <div className="mt-2 space-y-2 text-[10px]">
            <DiagBlock title="Tools IBKR exposes to your token" data={tools} />
            <DiagBlock title="Raw /positions response" data={positions} />
            <DiagBlock title="Raw /summary response" data={summary} />
            <div className="text-muted-foreground italic">
              Copy any of the blocks above and paste into Aanar chat so we can map IBKR's real tool names.
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

/* --------- helpers --------- */
function DiagBlock({ title, data }) {
  return (
    <div>
      <div className="text-[10px] font-mono-tab uppercase tracking-wider text-muted-foreground mb-1">{title}</div>
      <pre className="text-[10px] bg-black/40 p-2 rounded-md overflow-x-auto max-h-48 leading-tight">
        {data ? JSON.stringify(data, null, 2) : "(no data)"}
      </pre>
    </div>
  );
}

function Tile({ label, value, accent }) {
  const cls = accent === "gain" ? "gain" : accent === "loss" ? "loss" : "";
  return (
    <div className="rounded-lg bg-secondary/30 border border-border/40 p-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`font-mono-tab text-sm font-semibold mt-0.5 ${cls}`}>{value}</div>
    </div>
  );
}

function extractPositionsList(raw) {
  if (!raw) return null;
  // Wrapper shape: { tool, positions: <payload>, tried }
  const p = raw.positions ?? raw;
  if (Array.isArray(p)) return p;
  if (p && typeof p === "object") {
    for (const k of ["positions", "items", "data", "rows", "result"]) {
      if (Array.isArray(p[k])) return p[k];
    }
  }
  return [];
}

function extractSummary(raw) {
  const s = raw?.summary ?? raw ?? {};
  const pick = (obj, keys) => {
    for (const k of keys) {
      if (obj?.[k] != null) {
        const v = obj[k];
        const n = Number(v?.value ?? v);
        if (!Number.isNaN(n)) return n;
      }
    }
    return null;
  };
  return {
    netLiq: pick(s, ["net_liquidation", "netLiquidation", "netLiq", "nlv", "equity_with_loan_value", "totalValue"]),
    cash: pick(s, ["total_cash_value", "cash", "total_cash", "totalCash", "cashBalance", "available_funds"]),
    buyingPower: pick(s, ["buying_power", "buyingPower", "excess_liquidity"]),
    dayPnl: pick(s, ["day_pnl", "dayPnl", "unrealized_pnl", "realized_pnl", "dailyPnl"]),
  };
}
